import { Injectable } from '@angular/core';
import { PutTraceSegmentsCommandOutput, XRay } from '@aws-sdk/client-xray';
import { Segment, SegmentException } from '../types';

const crypto = window.crypto;

@Injectable()
export class XrayService {
  private readonly xray?: XRay;
  private readonly skip: boolean = false;

  constructor() {
    if (window.location.hostname.includes('localhost') || window.location.hostname === '127.0.0.1') {
      // tslint:disable-next-line:no-console
      console.log('Skipping load of XRay SDK for local development');
      this.skip = true;
      return;
    }
    if (!crypto) {
      // tslint:disable-next-line:no-console
      console.log('Skipping load of XRay SDK, because no window.crypto support in browser');
      this.skip = true;
      return;
    }
    if (!window['AWS_XRAY_ACCESS_KEY_ID'] || !window['AWS_XRAY_SECRET_ACCESS_KEY'] || !window['AWS_XRAY_SESSION_TOKEN']) {
      console.log('Skipping load of XRay SDK, because XRay client credentials are not passed');
      this.skip = true;
      return;
    }

    this.xray = new XRay({
      region: window['AWS_XRAY_REGION'] ?? 'us-east-1',
      credentials: {
        accessKeyId: window['AWS_XRAY_ACCESS_KEY_ID'],
        secretAccessKey: window['AWS_XRAY_SECRET_ACCESS_KEY'],
        sessionToken: window['AWS_XRAY_SESSION_TOKEN'],
      },
    });
  }

  public getTraceHeader(segment: Segment): string {
    if (this.skip) {
      return 'Unsupported';
    }
    return 'Root=' + segment.trace_id + ';Parent=' + segment.id + ';Sampled=1';
  }

  public beginSegment(method: string, url: string): Segment | undefined {
    if (this.skip) {
      return;
    }
    const id = this.getHexId(16);
    const startTime = this.getEpochTime();
    const traceId = '1-' + this.getHexTime() + '-' + this.getHexId(24);

    const segment: Segment = {
      id,
      trace_id: traceId,
      start_time: startTime,
      name: `ui-rest-call:${window.location.hostname}`,
      in_progress: true,
      user: sessionStorage['userid'],
      http: {
        request: {
          url: url,
          method: method.toUpperCase(),
          traced: true,
        },
      },
      annotations: {
        host: window.location.hostname,
        backend: window['VELO_API'] ?? '',
      },
    };

    const documents: string[] = [];
    documents[0] = JSON.stringify(segment);
    this.putDocuments(documents);
    return segment;
  }

  public endSegment(segment: Segment): void {
    if (this.skip || segment.in_progress === false) {
      return;
    }
    segment.end_time = this.getEpochTime();
    segment.in_progress = false;
    segment.http.response = {
      status: 200,
    };
    const documents: string[] = [];
    documents[0] = JSON.stringify(segment);
    this.putDocuments(documents);
  }

  public endSegmentWithError(segment: Segment, status?: number, error?: any): void {
    if (this.skip || segment.in_progress === false) {
      return;
    }
    segment.end_time = this.getEpochTime();
    segment.in_progress = false;
    segment.error = !!status && status >= 400 && status < 500;
    segment.throttle = status === 429;
    segment.fault = !status || (status >= 500 && status < 600);
    if (status) {
      segment.http.response = {
        status: status,
      };
    }
    if (error && error instanceof Error) {
      const e = error as Error;

      segment.cause = {
        working_directory: window.location.pathname,
        exceptions: [this.getSegmentException(e)],
      };
    } else if (error) {
      let s: string = '' + error;
      try {
        s = JSON.stringify(error); // prefer JSON representation to avoid typical: Object.object crap
      } catch (unused) {
        // tslint:disable-next-line:no-console
        console.log(`Failed to serialize error to JSON representation: ${error}`);
      } // cycles are not JSON supported, but might be present!

      const e = new Error(s);
      segment.cause = {
        working_directory: window.location.pathname,
        exceptions: [this.getSegmentException(e)],
      };
    }

    const documents: string[] = [];
    documents[0] = JSON.stringify(segment);
    this.putDocuments(documents);
  }

  private getHexId(length: number): string {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    let hex = '';
    for (const c of bytes) {
      hex += c.toString(16);
    }
    return hex.substring(0, length);
  }

  private getHexTime(): string {
    return Math.round(new Date().getTime() / 1000).toString(16);
  }

  private getEpochTime(): number {
    return new Date().getTime() / 1000;
  }

  private getSegmentException(error: Error): SegmentException {
    const stack = [];
    const frames = ('' + error.stack).split('\n');
    for (const f of frames) {
      stack.push({
        label: f,
      });
    }

    const hashCode = (s: string): number => {
      let hash = 0;
      if (s.length === 0) {
        return hash;
      }
      for (let i = 0; i < s.length; i++) {
        const chr = s.charCodeAt(i);
        // tslint:disable-next-line:no-bitwise
        hash = (hash << 5) - hash + chr;
        // tslint:disable-next-line:no-bitwise
        hash |= 0; // Convert to 32bit integer
      }
      return hash;
    };

    /* Convert value as 32-bit unsigned integer to 8 digit hexadecimal number. */
    const hex32 = (val: number): string => {
      // tslint:disable-next-line:no-bitwise
      val &= 0xffffffff;
      const hex = val.toString(16).toUpperCase();
      return ('00000000' + hex).slice(-8);
    };

    return {
      id: '00000000' + hex32(hashCode(error.message)),
      message: error.message,
      stack,
    };
  }

  private putDocuments(documents: string[]): void {
    const params = {
      TraceSegmentDocuments: documents,
    };
    if (!this.xray) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.xray.putTraceSegments(params, (err: any, data?: PutTraceSegmentsCommandOutput) => {
      if (err) {
        // tslint:disable-next-line:no-console
        console.log(err, err.stack);
      }
    });
  }
}
